import React from 'react';
import { FieldImage, Fit, ItemText, fs } from '../parts/fields';
import { Head, Logo } from '../parts/chrome';
import { img, items, useSlide } from '../parts/context';
import { DeckItem } from '../types';

const Check: React.FunctionComponent<{ size: number }> = ({ size }): React.ReactElement => (
    <span aria-hidden="true" style={{ flex: 'none', width: size, height: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'color-mix(in srgb, var(--wd-accent) 12%, transparent)', color: 'var(--wd-accent)' }}>
        <svg viewBox="0 0 24 24" width={size * 0.55} height={size * 0.55} fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
    </span>
);

const longest = (list: DeckItem[]): number => list.reduce((m, it) => Math.max(m, (it.body ?? '').length), 0);

// #region BulletsLayout
/** Two to six points. Without an image they sit in a grid; with one, a list beside the image. */
export const BulletsLayout: React.FunctionComponent = (): React.ReactElement => {
    const { slide, edit } = useSlide();
    const list = items(slide.fields.items);
    const withImage = edit || !!img(slide.fields.image);
    const n = list.length;
    const cols = withImage ? (n >= 5 ? 2 : 1) : n <= 3 ? n : n === 4 ? 2 : 3;
    // Beside an image the list is one narrow column, so each extra point steps the type down.
    const level = withImage ? (n <= 3 ? 0 : n === 4 ? 1 : 2) : (n > 4 || longest(list) > 160 ? 2 : 0);
    const dense = level > 0;
    const titleSize = [38, 34, 30][level];
    const imageWidth = n >= 5 ? 560 : 660;
    const bodySize = [28, 25, 23][level];
    return (
        <>
            <div className="wd-pad" style={{ right: withImage ? imageWidth + 100 : 0, paddingRight: withImage ? 96 : 128 }}>
                <Head />
                <Fit style={{ flex: 1 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, cols)}, minmax(0, 1fr))`, columnGap: withImage ? 48 : 72, rowGap: withImage ? (dense ? 34 : 44) : 64 }}>
                        {list.map((_, i) => (
                            <div key={i} style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
                                <Check size={dense ? 48 : 56} />
                                <div style={{ minWidth: 0, flex: 1, paddingTop: dense ? 4 : 8 }}>
                                    <ItemText index={i} part="title" className="wd-h" style={{ fontSize: fs(titleSize), lineHeight: 1.15 }} placeholder="Point" max={60} />
                                    <ItemText index={i} part="body" className="wd-lead wd-clamp" style={{ fontSize: fs(bodySize), marginTop: 12, color: 'var(--wd-muted)' }} placeholder="A line or two about it" max={240} />
                                </div>
                            </div>
                        ))}
                    </div>
                </Fit>
            </div>
            {withImage ? <FieldImage k="image" framed style={{ position: 'absolute', top: 72, right: 72, bottom: 72, width: imageWidth }} /> : null}
            <Logo />
        </>
    );
};
